/**
 * The words on the developers pages, generated from the numbers.
 *
 * Deterministic templates, no model in the loop: the same data always reads
 * the same way, and every sentence here is covered by story.test.ts.
 */

import {
  capitalize,
  countWord,
  delta,
  fmtHours,
  fmtNum,
  pctChange,
  periodName,
  plural,
  priorName,
  inPeriod,
} from "~/lib/format";

export interface ReviewConcentration {
  /** People who reviewed at least one PR. */
  reviewers: number;
  /** Distinct (reviewer, PR) pairs. */
  totalReviews: number;
  /** Fewest people who together did at least half the reviews. */
  topN: number;
  /** Share (0-100) the top N did. */
  topShare: number;
  /** Their ids, heaviest first. */
  topIds: string[];
}

export function reviewConcentration(
  people: Array<{ id: string; reviewsGiven: number }>,
): ReviewConcentration {
  const sorted = people
    .filter((p) => p.reviewsGiven > 0)
    .sort((a, b) => b.reviewsGiven - a.reviewsGiven);
  const total = sorted.reduce((s, p) => s + p.reviewsGiven, 0);
  let acc = 0;
  const topIds: string[] = [];
  for (const p of sorted) {
    if (total > 0 && acc * 2 >= total) break;
    acc += p.reviewsGiven;
    topIds.push(p.id);
  }
  return {
    reviewers: sorted.length,
    totalReviews: total,
    topN: topIds.length,
    topShare: total > 0 ? Math.round((acc / total) * 100) : 0,
    topIds,
  };
}

export interface TeamStoryInput {
  periodLabel: string;
  prevLabel: string;
  prsMerged: number;
  prevPrsMerged: number;
  activePeople: number;
  prevActivePeople: number;
  medianHoursToMerge: number | null;
  prevMedianHoursToMerge: number | null;
  concentration: ReviewConcentration;
}

/**
 * Review load counts as concentrated when half of it sits with four or fewer
 * people out of a group large enough for that to be a choice (six or more
 * reviewers), and when those few are no more than a third of reviewers.
 */
export function isConcentrated(c: ReviewConcentration): boolean {
  return c.reviewers >= 6 && c.topN <= 4 && c.topN * 3 <= c.reviewers;
}

export function buildHeadline(s: TeamStoryInput): string {
  const when = `in ${periodName(s.periodLabel)}`;
  let lead: string;
  const d = delta(s.prsMerged, s.prevPrsMerged, "up", { flatBand: 5, minBaseForPct: 1 });
  if (s.prsMerged === 0 && s.prevPrsMerged === 0) {
    lead = `No PRs merged ${when} yet`;
  } else if (s.prevPrsMerged === 0) {
    lead = `${fmtNum(s.prsMerged)} PRs merged ${when}, from none`;
  } else if (d.direction === "flat") {
    lead = `Merged PRs hold steady ${when} at ${fmtNum(s.prsMerged)}`;
  } else {
    lead = `Merged PRs ${d.direction} ${Math.abs(d.pct ?? 0)}% ${when}`;
  }

  if (s.prsMerged > 0 && isConcentrated(s.concentration)) {
    return `${lead}, with review load falling on ${countWord(s.concentration.topN)} ${s.concentration.topN === 1 ? "person" : "people"}`;
  }
  const ttm = pctChange(s.medianHoursToMerge, s.prevMedianHoursToMerge);
  if (ttm !== null && Math.abs(ttm) >= 25) {
    return ttm > 0
      ? `${lead}, and PRs taking ${ttm}% longer to merge`
      : `${lead}, and PRs merging ${Math.abs(ttm)}% faster`;
  }
  const people = s.activePeople - s.prevActivePeople;
  if (Math.abs(people) >= 3) {
    return `${lead}, with ${countWord(Math.abs(people))} ${people > 0 ? "more" : "fewer"} people contributing`;
  }
  return lead;
}

export function buildDek(s: TeamStoryInput): string {
  const prior = priorName(s.prevLabel);
  const parts: string[] = [];
  parts.push(
    `${plural(s.activePeople, "person", "people")} merged ${plural(s.prsMerged, "pull request")} ${periodPhrase(s.periodLabel)}, against ${fmtNum(s.prevPrsMerged)} over ${prior}.`,
  );
  if (s.medianHoursToMerge !== null) {
    const was =
      s.prevMedianHoursToMerge !== null
        ? `, ${s.medianHoursToMerge <= s.prevMedianHoursToMerge ? "down" : "up"} from ${fmtHours(s.prevMedianHoursToMerge)}`
        : "";
    parts.push(
      `The typical PR took ${fmtHours(s.medianHoursToMerge)} from open to merge${was}.`,
    );
  }
  const c = s.concentration;
  if (c.reviewers > 0) {
    parts.push(
      `${capitalize(countWord(c.topN))} of ${plural(c.reviewers, "reviewer")} did ${c.topShare}% of the reviewing.`,
    );
  }
  return parts.join(" ");
}

function periodPhrase(label: string): string {
  return inPeriod(label);
}

export function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName;
}

export interface PersonStoryInput {
  displayName: string;
  periodLabel: string;
  prevLabel: string;
  prsMerged: number;
  prevPrsMerged: number | null;
  reviewsGiven: number;
  prevReviewsGiven: number | null;
  teamMedianPrsMerged: number | null;
  teamMedianReviews: number | null;
}

export function buildPersonHeadline(s: PersonStoryInput): string {
  const who = firstName(s.displayName);
  const when = `in ${periodName(s.periodLabel)}`;
  const prior = priorName(s.prevLabel);
  if (s.prsMerged === 0 && s.reviewsGiven === 0) {
    return `${who} has no merged or reviewed PRs ${when} yet`;
  }
  if (s.prsMerged === 0) {
    return `${who} reviewed ${plural(s.reviewsGiven, "PR")} ${when} and merged none`;
  }
  const merged = `${who} merged ${plural(s.prsMerged, "PR")} ${when}`;
  if (s.prevPrsMerged === null || s.prevPrsMerged === undefined) {
    return `${merged}, with no merges over ${prior}`;
  }
  const diff = s.prsMerged - s.prevPrsMerged;
  if (diff === 0) return `${merged}, the same as ${prior}`;
  return `${merged}, ${diff > 0 ? "up" : "down"} ${fmtNum(Math.abs(diff))} from ${prior}`;
}

export function buildPersonDek(s: PersonStoryInput): string {
  const parts: string[] = [];
  if (s.teamMedianPrsMerged !== null) {
    const rel = compareToMedian(s.prsMerged, s.teamMedianPrsMerged);
    parts.push(
      `That is ${rel} the team median of ${fmtNum(s.teamMedianPrsMerged)} merged PRs.`,
    );
  }
  if (s.teamMedianReviews !== null) {
    parts.push(
      `${capitalize(firstName(s.displayName))} reviewed ${plural(s.reviewsGiven, "PR")} by other people, against a median of ${fmtNum(s.teamMedianReviews)}.`,
    );
  }
  return parts.join(" ");
}

export function compareToMedian(value: number, med: number): string {
  if (med === 0) return value === 0 ? "level with" : "above";
  const ratio = value / med;
  if (ratio >= 1.95) return `${fmtNum(Math.round(ratio * 10) / 10)} times`;
  if (ratio > 1.1) return "above";
  if (ratio >= 0.9) return "level with";
  return "below";
}
